import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { EventSeat } from '@/components/events/event-seat-answers'

type SeatsOrder = {
  quantity: number
  seats: EventSeat[]
  invoice: null | { id?: number; status: string; balance: string; total: string }
  money_state?: { status: string; open_balance_cents: number }
}

/** Every seat on an Order, who holds it, and the invoice that pays for it. */
export function EventOrderSeats({ order }: { order: SeatsOrder }) {
  const seats = order.seats ?? []
  return (
    <section aria-label="Order seats" className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <p className="text-body-sm font-medium text-foreground">{seatSummary(order, seats)}</p>
        <OrderInvoiceSummary order={order} />
      </div>
      {seats.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Seat</TableHead>
                <TableHead>Attendee</TableHead>
                <TableHead>Email</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {seats.map((seat, index) => (
                <TableRow key={seat.id}>
                  <TableCell className="tabular-nums text-muted-foreground">{index + 1}</TableCell>
                  <TableCell>
                    {seat.name || <Badge variant="warning">Awaiting name</Badge>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{seat.email || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  )
}

function OrderInvoiceSummary({ order }: { order: SeatsOrder }) {
  const invoice = order.invoice
  if (!invoice) return <p className="text-caption text-muted-foreground">Free order</p>
  const badge = invoiceBadge(order, invoice.status)
  return (
    <div className="flex flex-wrap items-center gap-2 text-body-sm">
      <span className="font-semibold tabular-nums text-foreground">{invoice.total}</span>
      <Badge variant={badge.variant}>{badge.label}</Badge>
      {badge.label === 'Outstanding' && (
        <span className="text-caption text-muted-foreground">{invoice.balance} outstanding</span>
      )}
      {invoice.id !== undefined && (
        <a
          href={`/finance/invoices/${invoice.id}`}
          className="text-body-sm font-medium text-primary hover:underline"
        >
          View invoice
        </a>
      )}
    </div>
  )
}

function invoiceBadge(order: SeatsOrder, status: string) {
  if (status === 'Voided') return { label: 'Voided', variant: 'neutral' } as const
  const open = order.money_state ? order.money_state.status === 'open' : status !== 'Paid'
  return open
    ? ({ label: 'Outstanding', variant: 'warning' } as const)
    : ({ label: 'Paid', variant: 'success' } as const)
}

function seatSummary(order: SeatsOrder, seats: EventSeat[]) {
  const named = seats.filter((seat) => seat.name).length
  const awaiting = Math.max(order.quantity - named, 0)
  return `${plural(order.quantity, 'seat')}, ${named} named, ${awaiting} awaiting ${
    awaiting === 1 ? 'name' : 'names'
  }`
}

function plural(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}
